/**
 * Public REST API DTOs for `/api/v1` on wiki-gateway.
 *
 * Two halves:
 *  1. Desktop-compatible surface — request/response bodies byte-compatible
 *     with the desktop local API (vendor/upstream/src-tauri/src/api_server.rs,
 *     inventory 08 §4.3, features F-254..F-262) so the existing MCP stdio
 *     server and any desktop-API client keep working against the gateway.
 *  2. Server extensions — auth/tenancy/token/project CRUD, clip, uploads,
 *     jobs, media, events (ARCHITECTURE §3).
 *
 * Every response keeps the `{ ok: true, ... } | { ok: false, error }`
 * envelope (core.ts).
 */
import { z } from "zod";
import { ApiFileNodeSchema, ConversationSchema, DedupTaskSchema, DisplayMessageSchema, DuplicateGroupSchema, EpochMsSchema, ErrorEnvelopeSchema, FileChangeQueueSchema, FileChangeRescanResultSchema, IngestTaskSchema, GraphEdgeSchema, GraphNodeSchema, IsoDateTimeSchema, JsonObjectSchema, LintItemSchema, MessageImageSchema, ProjectEntrySchema, ProjectRefSchema, ProjectRelPathSchema, ProjectSearchResultSchema, ReviewStatusFilterSchema, SanitizedReviewItemSchema, SearchModeSchema, UuidSchema, } from "./core.js";
import { ModelOverrideSchema } from "./config.js";
import { TenantQuotasSchema } from "./jobs.js";
export { ErrorEnvelopeSchema, ProjectRefSchema };
// ─── Constants (api_server.rs:18-32, preserved as per-tenant defaults) ───
export const API_PREFIX = "/api/v1";
/** Request bodies larger than this are rejected (desktop parity). */
export const MAX_BODY_BYTES = 1024 * 1024;
/** `GET /files/content` size cap (desktop parity). */
export const MAX_FILE_CONTENT_BYTES = 2 * 1024 * 1024;
export const DEFAULT_MAX_FILES = 2000;
export const HARD_MAX_FILES = 10000;
export const DEFAULT_MAX_REVIEWS = 200;
export const HARD_MAX_REVIEWS = 1000;
export const MAX_SEARCH_RESULTS = 50;
/** Token acceptance channels (F-253): header, bearer, query param. */
export const API_TOKEN_HEADER = "x-llm-wiki-token";
export const API_TOKEN_QUERY_PARAM = "token";
// ─── GET /health (F-254) ─────────────────────────────────────────────────
export const ApiServerStatusSchema = z.enum([
    "starting",
    "running",
    "port_conflict",
    "error",
]);
/**
 * Desktop-compatible health body. Server semantics:
 *  - `enabled` / `mcpEnabled` are the caller-tenant flags (or global
 *    defaults when unauthenticated).
 *  - `tokenSource`: desktop reported env|store|none; the server reports
 *    how the presented credential resolved.
 *  - `allowUnauthenticated` is always `false` (dropped feature, field
 *    kept for wire compatibility with the MCP client).
 */
export const HealthResponseSchema = z.object({
    ok: z.literal(true),
    status: ApiServerStatusSchema,
    version: z.string(),
    authRequired: z.boolean(),
    authConfigured: z.boolean(),
    tokenSource: z.enum(["env", "store", "none", "api-token", "session"]),
    enabled: z.boolean(),
    mcpEnabled: z.boolean(),
    allowUnauthenticated: z.boolean(),
});
// ─── GET /projects (F-255) ───────────────────────────────────────────────
export const ProjectsResponseSchema = z.object({
    ok: z.literal(true),
    projects: z.array(ProjectEntrySchema),
    /** The caller's `current` project (per-user last-opened) or null. */
    currentProject: ProjectEntrySchema.nullable(),
});
// ─── GET /projects/{id}/files (F-256) ────────────────────────────────────
export const FilesRootSchema = z.enum(["wiki", "sources", "raw", "raw/sources", "all"]);
export const FilesQuerySchema = z.object({
    root: FilesRootSchema.default("wiki"),
    /** Anything except the literal "false" means true (desktop parity). */
    recursive: z
        .string()
        .optional()
        .transform((v) => v !== "false"),
    maxFiles: z.coerce
        .number()
        .int()
        .optional()
        .transform((v) => Math.min(Math.max(v ?? DEFAULT_MAX_FILES, 1), HARD_MAX_FILES)),
});
export const FilesResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    /** `wiki` | `raw/sources` | `all` (normalized server-side). */
    root: z.string(),
    files: z.array(ApiFileNodeSchema),
    truncated: z.boolean(),
});
// ─── GET /projects/{id}/files/content (F-257) ────────────────────────────
export const FileContentQuerySchema = z.object({
    path: ProjectRelPathSchema,
    /**
     * Dashboard extension (fs-api readFile extractImages option): when
     * truthy, extraction also materializes embedded images beside the
     * source (desktop read_file parity). Ignored for plain text files.
     */
    extract: z.string().optional(),
});
export const FileContentResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    path: z.string(),
    content: z.string(),
});
/** Text-extension allowlist for /files/content (desktop parity). */
export const TEXT_CONTENT_EXTENSIONS = [
    "md", "mdx", "txt", "csv", "json", "yaml", "yml", "xml", "html", "htm",
    "rtf", "log",
];
// ─── GET /projects/{id}/reviews (F-258) ──────────────────────────────────
export const ReviewsQuerySchema = z.object({
    status: ReviewStatusFilterSchema.default("unresolved"),
    type: z.string().trim().min(1).optional(),
    /**
     * `full=1` returns the UNsanitized vendored `ReviewItem` shape
     * (options + action strings) for the dashboard review inbox restore
     * (F-230). Default (public API surface, F-258): sanitized items.
     */
    full: z.enum(["0", "1", "true", "false"]).optional(),
    limit: z.coerce
        .number()
        .int()
        .optional()
        .transform((v) => Math.min(Math.max(v ?? DEFAULT_MAX_REVIEWS, 1), HARD_MAX_REVIEWS)),
});
export const ReviewsResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    /** Reported as `unresolved` even when requested as `pending`. */
    status: z.enum(["unresolved", "resolved", "all"]),
    count: z.number().int().nonnegative(),
    reviews: z.array(SanitizedReviewItemSchema),
});
// ─── POST /projects/{id}/search (F-259) ──────────────────────────────────
export const SearchRequestSchema = z.object({
    query: z.string().trim().min(1, "query is required"),
    topK: z.number().int().min(1).max(MAX_SEARCH_RESULTS).optional(),
    includeContent: z.boolean().optional(),
    queryEmbedding: z
        .array(z.number().finite())
        .nonempty()
        .optional(),
});
export const SearchResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    mode: SearchModeSchema,
    note: z.string(),
    tokenHits: z.number().int().nonnegative(),
    vectorHits: z.number().int().nonnegative(),
    results: z.array(ProjectSearchResultSchema),
});
// ─── GET /projects/{id}/graph (F-260) ────────────────────────────────────
export const GraphQuerySchema = z.object({
    q: z.string().optional(),
    nodeType: z.string().optional(),
    limit: z.coerce
        .number()
        .int()
        .optional()
        .transform((v) => Math.min(Math.max(v ?? 200, 1), 10000)),
});
export const GraphResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    nodes: z.array(GraphNodeSchema),
    edges: z.array(GraphEdgeSchema),
    /** First build of the graph still running (HTTP 202): retry shortly. */
    building: z.boolean().optional(),
    progress: z.object({ done: z.number(), total: z.number() }).nullable().optional(),
    /** Served from the cache while a refresh is still running. */
    stale: z.boolean().optional(),
    error: z.string().optional(),
});
// ─── POST /projects/{id}/sources/rescan (F-261) ──────────────────────────
export const RescanResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    result: FileChangeRescanResultSchema,
});
// ─── POST /projects/{id}/chat (F-262 — real implementation, SSE) ────────
/**
 * Replaces the desktop 501 stub. The response is an SSE stream of
 * `chat.token` / `chat.reasoning` / `chat.references` / `chat.done` /
 * `chat.error` events (see ./events), scoped to the returned streamId.
 */
export const ChatRequestSchema = z.object({
    message: z.string().min(1),
    /** Omit to create a new conversation. */
    conversationId: z.string().optional(),
    /** Vision input attached to the user message. */
    images: z.array(MessageImageSchema).optional(),
    /** History window override (desktop maxHistoryMessages). */
    maxHistoryMessages: z.number().int().min(1).max(1000).optional(),
    /**
     * Mix external web-search results into retrieval (chat-panel's
     * "web search" toggle; the search itself runs server-side against
     * the tenant's searchApiConfig). AnyTXT (desktop-only) is dropped.
     */
    useWebSearch: z.boolean().optional(),
    /**
     * Chat-only provider+model override (dashboard model selector). Applies to
     * this turn alone; the project's stored llmConfig (which also drives
     * ingest) is untouched. The runtime reads the API key from
     * providerConfigs[provider].
     */
    modelOverride: ModelOverrideSchema.optional(),
});
// ─── Chat image attachment caps (F-170, chat-image-utils.ts verbatim) ────
//
// The desktop enforced these client-side only; the server re-validates on
// intake so a direct API caller cannot exceed the desktop limits.
export const CHAT_ACCEPTED_IMAGE_TYPES = [
    "image/png",
    "image/jpeg",
    "image/webp",
    "image/gif",
];
/** Per-image decoded size cap (5 MB, chat-image-utils MAX_IMAGE_BYTES). */
export const CHAT_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
/** Max images attachable to a single message (MAX_IMAGES_PER_MESSAGE). */
export const CHAT_MAX_IMAGES_PER_MESSAGE = 5;
/**
 * `POST /projects/{id}/chat/regenerate` — drop the last assistant turn
 * and re-run the last user turn (desktop handleRegenerate). Same SSE
 * stream contract as POST /chat.
 */
export const RegenerateChatRequestSchema = z.object({
    /** Omitted → the active conversation (desktop behavior). */
    conversationId: z.string().optional(),
    maxHistoryMessages: z.number().int().min(1).max(1000).optional(),
    useWebSearch: z.boolean().optional(),
    /** Chat-only provider+model override — as POST /chat. */
    modelOverride: ModelOverrideSchema.optional(),
});
/** First SSE event / JSON preamble identifying the stream. */
export const ChatAcceptedSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    conversationId: z.string(),
    streamId: z.string(),
});
// ─── Conversations (dashboard chat history) ──────────────────────────────
export const ConversationsResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    conversations: z.array(ConversationSchema),
});
export const ConversationMessagesResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    conversationId: z.string(),
    messages: z.array(DisplayMessageSchema),
});
/** `DELETE /projects/{id}/conversations/{id}` (F-197 sidebar delete). */
export const DeleteConversationResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    conversationId: z.string(),
    deleted: z.boolean(),
});
// ─── POST /projects/{id}/clip + POST /clip (F-263/F-264) ─────────────────
/**
 * Browser-extension clip. Body mirrors the desktop clip server's
 * `POST /clip` (clip_server.rs): the write logic (slug ≤50 chars,
 * `-YYYYMMDD`(-N) dedup suffixes, `type: clip` + `origin: web-clip`
 * frontmatter) is preserved byte-for-byte in the gateway handler.
 * `projectPath` is accepted for extension backward-compat; the
 * user-default project applies on `POST /api/v1/clip`.
 */
export const ClipRequestSchema = z.object({
    title: z.string(),
    url: z.string(),
    content: z.string(),
    projectPath: z.string().optional(),
});
export const ClipResponseSchema = z.object({
    ok: z.literal(true),
    /** Project-relative path of the written clip markdown file. */
    path: z.string(),
});
// ─── GET /projects/{id}/media (F-169) ────────────────────────────────────
/**
 * Binary media proxy. `src` is the raw value from the markdown image
 * ref (wiki-relative or legacy absolute); resolver containment rules
 * are the authz check. Response is bytes, not JSON.
 */
export const MediaQuerySchema = z.object({
    src: z.string().min(1),
});
// ─── GET /projects/{id}/events (SSE, §10.2) ──────────────────────────────
export const EventsQuerySchema = z.object({
    /** Alternative to the Last-Event-ID header for EventSource polyfills. */
    lastEventId: z.coerce.number().int().nonnegative().optional(),
    /** Comma-separated event-type prefix filter, e.g. `chat.,activity.` */
    types: z.string().optional(),
});
// ─── Auth: sessions (ARCHITECTURE §3) ────────────────────────────────────
export const UserRoleSchema = z.enum(["owner", "admin", "member"]);
export const UserSchema = z.object({
    id: UuidSchema,
    tenantId: UuidSchema,
    email: z.string().email(),
    role: UserRoleSchema,
    uiPrefs: JsonObjectSchema.default({}),
    tz: z.string().default("UTC"),
    createdAt: IsoDateTimeSchema.optional(),
});
export const TenantSchema = z.object({
    id: UuidSchema,
    name: z.string().min(1),
    createdAt: IsoDateTimeSchema,
    planLimits: JsonObjectSchema.default({}),
});
export const LoginRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
/** Session cookie is set httpOnly; csrfToken echoes on mutations. */
export const LoginResponseSchema = z.object({
    ok: z.literal(true),
    user: UserSchema,
    csrfToken: z.string(),
});
export const MeResponseSchema = z.object({
    ok: z.literal(true),
    user: UserSchema,
    tenant: TenantSchema,
});
export const ChangePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(10),
});
export const CSRF_HEADER = "x-csrf-token";
// ─── Auth: API tokens (F-139/F-241/F-253) ────────────────────────────────
export const ApiTokenScopeSchema = z.enum(["read", "write", "admin"]);
export const ApiTokenSchema = z.object({
    id: UuidSchema,
    userId: UuidSchema,
    name: z.string().min(1),
    scopes: z.array(ApiTokenScopeSchema).nonempty(),
    /** Restrict token to one project; null = all caller-visible projects. */
    projectId: UuidSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    lastUsed: IsoDateTimeSchema.nullable(),
    /** First 8 chars of the secret, to recognise a token without exposing it. */
    prefix: z.string().optional(),
    /** Email of the user who owns it (project listings can show others'). */
    ownerEmail: z.string().optional(),
});
export const CreateApiTokenRequestSchema = z.object({
    name: z.string().min(1).max(120),
    scopes: z.array(ApiTokenScopeSchema).nonempty().default(["read"]),
    projectId: UuidSchema.nullable().optional(),
});
/** `token` (base64url, 256-bit) is returned exactly once at creation. */
export const CreateApiTokenResponseSchema = z.object({
    ok: z.literal(true),
    apiToken: ApiTokenSchema,
    token: z.string(),
});
export const ListApiTokensResponseSchema = z.object({
    ok: z.literal(true),
    apiTokens: z.array(ApiTokenSchema),
    /** Project listings: the caller administers the workspace (sees/revokes all). */
    canManageAll: z.boolean().optional(),
});
// ─── Tenancy: project CRUD (§3, §4) ──────────────────────────────────────
/**
 * 'provisioning' is a transient server-internal state (row inserted, disk
 * scaffold not yet complete — security review F9); clients normally only
 * ever see 'active' | 'archived'.
 */
export const ProjectStatusSchema = z.enum(["provisioning", "active", "archived"]);
export const ProjectSchema = z.object({
    id: UuidSchema,
    tenantId: UuidSchema,
    name: z.string().min(1),
    /** Path relative to the tenant root (`projects/<uuid>`). */
    relPath: z.string(),
    template: z.string().nullable(),
    status: ProjectStatusSchema,
    createdAt: IsoDateTimeSchema,
});
export const CreateProjectRequestSchema = z.object({
    name: z.string().min(1).max(200),
    template: z.string().optional(),
});
export const CreateProjectResponseSchema = z.object({
    ok: z.literal(true),
    project: ProjectSchema,
});
export const UpdateProjectRequestSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    // clients may only toggle active/archived — never 'provisioning' (F9)
    status: z.enum(["active", "archived"]).optional(),
});
/** `POST /projects/{id}/open` — records per-user MRU (F-106). */
export const OpenProjectResponseSchema = z.object({
    ok: z.literal(true),
    project: ProjectSchema,
    lastOpenedAt: IsoDateTimeSchema,
});
// ─── Uploads & desktop-project import (§4) ───────────────────────────────
/**
 * `POST /projects/{id}/sources` — multipart upload of source files into
 * `raw/sources/` (replaces desktop manual/folder import, F-119/120).
 * `relPath` preserves folder structure for folder uploads; collision
 * naming (`name-YYYYMMDD(-N).ext`) is applied server-side.
 */
export const UploadSourcesResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    imported: z.array(z.object({
        /** Final project-relative destination under raw/sources/. */
        path: z.string(),
        /** Ingest-queue task id when auto-ingest fired, else null. */
        taskId: z.string().nullable(),
    })),
    rejected: z.array(z.object({
        name: z.string(),
        reason: z.string(),
    })),
});
/** `POST /projects/import` — zip of a desktop project folder. */
export const ImportProjectResponseSchema = z.object({
    ok: z.literal(true),
    project: ProjectSchema,
    /** UUID adopted from `.llm-wiki/project.json` when valid. */
    adoptedProjectId: z.boolean(),
    warnings: z.array(z.string()),
});
// ─── Source deletion (F-121 cascade, via gateway → runtime) ──────────────
export const DeleteSourcesRequestSchema = z.object({
    paths: z.array(ProjectRelPathSchema).nonempty(),
    logReason: z.string().optional(),
});
export const DeleteSourcesResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    deletedWikiPaths: z.array(z.string()),
    rewrittenSourcePages: z.array(z.string()),
    skippedPages: z.array(z.string()),
});
// ─── Jobs read API (§8.4, §10.3) ─────────────────────────────────────────
export const ListJobsQuerySchema = z.object({
    kind: z.string().optional(),
    status: z.string().optional(),
    limit: z.coerce
        .number()
        .int()
        .optional()
        .transform((v) => Math.min(Math.max(v ?? 50, 1), 500)),
    before: IsoDateTimeSchema.optional(),
});
/** `GET /jobs/{id}/events` pages the durable job_events log (G-7). */
export const ListJobEventsQuerySchema = z.object({
    afterSeq: z.coerce.number().int().nonnegative().default(0),
    limit: z.coerce
        .number()
        .int()
        .optional()
        .transform((v) => Math.min(Math.max(v ?? 200, 1), 1000)),
});
// ─── Research / dedup / lint surfaces ────────────────────────────────────
export const StartResearchRequestSchema = z.object({
    topic: z.string().min(1),
    /**
     * Optimized web-search queries from the knowledge-gap flow (F-146).
     * Omitted → the engine researches with `[topic]` (desktop behavior).
     */
    searchQueries: z.array(z.string().min(1)).max(10).optional(),
});
export const StartResearchResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    taskId: z.string(),
    jobId: UuidSchema,
});
/** `POST /projects/{id}/research/{taskId}/cancel` (queued tasks only). */
export const CancelResearchResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    taskId: z.string(),
    cancelled: z.boolean(),
});
/**
 * `POST /projects/{id}/research/optimize` — knowledge-gap → optimized
 * research topic + search queries (F-146, vendored
 * optimize-research-topic.ts) run server-side with the real LLM key.
 */
export const OptimizeResearchTopicRequestSchema = z.object({
    gapTitle: z.string().min(1),
    gapDescription: z.string(),
    gapType: z.string(),
    /** Read from wiki/overview.md / wiki/purpose.md when omitted. */
    overview: z.string().optional(),
    purpose: z.string().optional(),
});
export const OptimizeResearchTopicResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    topic: z.string(),
    searchQueries: z.array(z.string()),
});
// ─── Dedup suite (F-036…F-043) ───────────────────────────────────────────
/** `POST /projects/{id}/dedup/scan` — durable scan job (default). */
export const StartDedupScanRequestSchema = z.object({
    /**
     * True runs the scan synchronously and returns the groups in the
     * response (patient API/MCP callers). Default: background job.
     */
    sync: z.boolean().optional(),
});
export const StartDedupScanResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    /** jobs-table row (kind `dedup-scan`); groups land in its summary. */
    jobId: UuidSchema,
});
/** Synchronous scan response (`sync: true`). */
export const DedupGroupsResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    groups: z.array(DuplicateGroupSchema),
});
/** `POST /projects/{id}/dedup/scan/{jobId}/cancel` */
export const CancelDedupScanResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    jobId: UuidSchema,
    cancelled: z.boolean(),
});
/** `POST /projects/{id}/dedup/merges` — enqueue a confirmed-group merge. */
export const EnqueueDedupMergeRequestSchema = z.object({
    group: DuplicateGroupSchema,
    canonicalSlug: z.string().min(1),
});
export const EnqueueDedupMergeResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    taskId: z.string(),
});
/** `POST /projects/{id}/dedup/not-duplicates` — whitelist (F-039). */
export const MarkNotDuplicatesRequestSchema = z.object({
    slugs: z.array(z.string()).min(2),
});
/** `POST /projects/{id}/dedup/actions` — merge-queue task control. */
export const DedupQueueActionRequestSchema = z.object({
    action: z.enum(["retry", "cancel"]),
    taskId: z.string().min(1),
});
/** Dedup merge-queue snapshot (mirrors IngestQueueResponse). */
export const DedupQueueResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    tasks: z.array(DedupTaskSchema),
    processing: z.boolean(),
});
// ─── Lint (F-044/F-045) ──────────────────────────────────────────────────
/** `POST /projects/{id}/lint/run` body (F-045 semantic toggle). */
export const LintRunRequestSchema = z.object({
    /**
     * `true` forces structural + semantic, `false` structural only;
     * omitted → semantic iff a usable LLM is configured (desktop default).
     */
    semantic: z.boolean().optional(),
});
/** `POST /projects/{id}/lint/run` — structural + semantic (server key). */
export const LintRunResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    started: z.boolean(),
});
/** `GET /projects/{id}/lint` — results (live updates via `lint.updated`). */
export const LintListResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    items: z.array(LintItemSchema),
});
// ─── Wiki-page lifecycle (F-047/F-053/F-054) ─────────────────────────────
/**
 * `POST /projects/{id}/pages/delete` — cascade delete with reference
 * sweep (embeddings + media dirs + wikilink rewrite in surviving pages).
 */
export const CascadeDeletePagesRequestSchema = z.object({
    paths: z.array(ProjectRelPathSchema).nonempty(),
});
export const CascadeDeletePagesResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    deletedPaths: z.array(z.string()),
    rewrittenFiles: z.number().int().nonnegative(),
});
/** `POST /projects/{id}/pages/enrich` — post-save wikilink enrichment. */
export const EnrichPageRequestSchema = z.object({
    path: ProjectRelPathSchema,
});
export const EnrichPageResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    enriched: z.boolean(),
});
// ─── Interactive ingest (F-024) ──────────────────────────────────────────
/**
 * `POST /projects/{id}/ingest/interactive` — start the interactive
 * ingest chat (vendored startIngest). Tokens stream over the project
 * events SSE as `chat.token`/`chat.done`/`chat.error`, filtered by the
 * returned streamId; nothing is written until `/writes`.
 */
export const StartInteractiveIngestRequestSchema = z.object({
    sourcePath: ProjectRelPathSchema,
});
export const StartInteractiveIngestResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    streamId: z.string(),
});
/** `POST /projects/{id}/ingest/interactive/{streamId}/writes` */
export const ExecuteIngestWritesResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    filesWritten: z.array(z.string()),
});
// ─── Embeddings re-index (F-091) ─────────────────────────────────────────
/** `POST /projects/{id}/embeddings/reindex` */
export const EmbeddingsReindexRequestSchema = z.object({
    /** True = desktop "re-index all" (clears the chunk table first). */
    clearExisting: z.boolean().optional(),
});
export const EmbeddingsReindexResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    /** jobs-table row (kind `embedding-reindex`). */
    jobId: UuidSchema,
});
/**
 * `GET /projects/{id}/embeddings` — vector-index stats for the Settings
 * embedding section (F-234): v2 chunk count + legacy v1 table row count
 * (rustd `vector_count_chunks` / `vector_legacy_row_count`).
 */
export const EmbeddingsStatsResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    chunkCount: z.number().int().nonnegative(),
    legacyRows: z.number().int().nonnegative(),
    /** Pages queued for the background embedding worker (absent if unknown). */
    pendingPages: z.number().int().nonnegative().optional(),
});
/**
 * `GET /projects/{id}/rotation-status` — per-slot LLM credential-rotation
 * state for the Settings panel (multi-token cooldowns, ARCHITECTURE §11.6).
 * `untilMs` is the cooldown deadline (epoch ms); `slots` is empty and
 * `total` is 0/1 when rotation is not active. Never carries token material.
 */
export const RotationSlotSchema = z.object({
    slot: z.number().int().positive(),
    cooling: z.boolean(),
    untilMs: z.number(),
    uses: z.number().int().nonnegative(),
    failures: z.number().int().nonnegative(),
});
export const RotationStatusResponseSchema = z.object({
    ok: z.literal(true),
    total: z.number().int().nonnegative(),
    slots: z.array(RotationSlotSchema),
});
/** `POST /projects/{id}/embeddings/drop-legacy` (F-234 legacy migration). */
export const DropLegacyEmbeddingsResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
});
// ─── Source folder delete (F-115 folder variant) ─────────────────────────
/** `POST /projects/{id}/sources/delete-folder` */
export const DeleteSourceFolderRequestSchema = z.object({
    /** Folder path relative to project root, under raw/sources/. */
    path: ProjectRelPathSchema,
    /** True when the folder already vanished on disk (external delete). */
    folderAlreadyDeleted: z.boolean().optional(),
});
export const DeleteSourceFolderResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    deletedWikiPaths: z.array(z.string()),
});
export const ResolveReviewRequestSchema = z.object({
    action: z.string().min(1),
});
// ─── Page write API (dashboard editor → runtime, fenced write path) ──────
export const WritePageRequestSchema = z.object({
    path: ProjectRelPathSchema,
    content: z.string(),
});
export const WritePageResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    path: z.string(),
});
// ─── Ingest queue control ────────────────────────────────────────────────
export const EnqueueIngestRequestSchema = z.object({
    paths: z.array(ProjectRelPathSchema).nonempty(),
});
export const EnqueueIngestResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    taskIds: z.array(z.string()),
});
// ─── User project state (recents / MRU, F-106) ───────────────────────────
export const UserProjectStateSchema = z.object({
    userId: UuidSchema,
    projectId: UuidSchema,
    lastOpenedAt: IsoDateTimeSchema,
});
export const RecentProjectsResponseSchema = z.object({
    ok: z.literal(true),
    recents: z.array(z.object({
        project: ProjectSchema,
        lastOpenedAt: IsoDateTimeSchema,
    })),
});
// ─── Connection tests (F-084, rate-limited per tenant) ───────────────────
export const ConnectionTestTargetSchema = z.enum([
    "llm",
    "embedding",
    "search",
    "multimodal",
    "mineru",
]);
export const ConnectionTestResponseSchema = z.object({
    ok: z.literal(true),
    target: ConnectionTestTargetSchema,
    success: z.boolean(),
    latencyMs: z.number().int().nonnegative().optional(),
    message: z.string().optional(),
});
/**
 * `POST /connection-tests/{target}` body (F-157). The test runs
 * SERVER-side (the desktop's Rust-fetch CORS dodge has no browser
 * equivalent). `config` optionally carries the UNSAVED settings-form
 * value so "Test" works before "Save" — redacted secret markers fall
 * back to the stored config.
 */
export const ConnectionTestRequestSchema = z.object({
    tenantId: UuidSchema.optional(),
    projectId: UuidSchema.optional(),
    config: JsonObjectSchema.optional(),
    /**
     * Vendored connection-tests.ts distinguishes a reachability probe
     * (`testLlmConnection`/`testEmbeddingConnection`) from a capability
     * probe (`testLlmFunction`/`testEmbeddingFunction`). Default: connection.
     */
    mode: z.enum(["connection", "function"]).optional(),
    /**
     * Which SAVED config fills a blank/redacted secret: the project's
     * effective one (default) or the workspace default. Testing the workspace
     * form must never borrow a project override's key.
     */
    configScope: z.enum(["project", "tenant"]).optional(),
});
// ─── Config REST surface (see ./config for value schemas) ────────────────
export const GetConfigResponseSchema = z.object({
    ok: z.literal(true),
    key: z.string(),
    /** Resolved value (project > user > tenant); secrets redacted. */
    value: z.unknown(),
    scope: z.enum(["tenant", "user", "project"]).nullable(),
});
export const SetConfigRequestSchema = z.object({
    value: z.unknown(),
    /** Target scope; must be allowed for the key (config.ts registry). */
    scope: z.enum(["tenant", "user", "project"]),
    projectId: UuidSchema.optional(),
});
// ─── Dashboard fs-funnel extensions (ADR-007 §alias fidelity) ────────────
//
// The vendored UI's `@/commands/fs` funnel needs a handful of primitives
// beyond the desktop-compatible read surface: stat, base64 write, copy,
// generic delete, related-wiki-page lookup. These are additive server
// extensions consumed by packages/dashboard/src/adapters/fs-api.ts.
/** `GET /projects/{id}/files/stat?path=&md5=1` */
export const FileStatQuerySchema = z.object({
    path: ProjectRelPathSchema,
    /** Anything except the literal "0"/"false" requests an md5 digest. */
    md5: z.string().optional(),
});
export const FileStatResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    path: z.string(),
    exists: z.boolean(),
    isDir: z.boolean(),
    /** Bytes; 0 when the file does not exist. */
    size: z.number().int().nonnegative(),
    /** Epoch ms; 0 when the file does not exist. */
    modifiedMs: z.number().int().nonnegative(),
    /** Hex md5, present when requested and the file exists. */
    md5: z.string().nullable(),
});
/** `POST /projects/{id}/files/base64` — binary write (image paste etc.). */
export const WriteFileBase64RequestSchema = z.object({
    path: ProjectRelPathSchema,
    base64: z.string(),
});
/** `POST /projects/{id}/files/copy` — in-project copy (file or dir). */
export const CopyFilesRequestSchema = z.object({
    from: ProjectRelPathSchema,
    to: ProjectRelPathSchema,
});
export const CopyFilesResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    /** Project-relative destination paths written (1 for a file copy). */
    copied: z.array(z.string()),
});
/**
 * `POST /projects/{id}/files/delete` — generic jailed delete (wiki pages,
 * persisted chat files, …). Source deletion with cascade stays on
 * `POST /sources/delete` (F-121).
 */
export const DeleteFilesRequestSchema = z.object({
    paths: z.array(ProjectRelPathSchema).nonempty(),
});
export const DeleteFilesResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    deleted: z.array(z.string()),
});
/** `GET /projects/{id}/sources/related?name=` (find_related_wiki_pages). */
export const RelatedWikiPagesQuerySchema = z.object({
    name: z.string().min(1),
});
export const RelatedWikiPagesResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    /** Project-relative wiki page paths referencing the source. */
    pages: z.array(z.string()),
});
// ─── File-change queue REST mirror (file-sync funnel, F-214) ─────────────
/** `GET /projects/{id}/file-changes` — initial hydration; live via SSE. */
export const FileChangeQueueResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    queue: FileChangeQueueSchema,
});
/** `POST /projects/{id}/file-changes/{taskId}/{retry|ignore}` */
export const FileChangeTaskActionSchema = z.enum(["retry", "ignore"]);
// ─── Ingest-queue REST mirror (activity panel, F-026) ────────────────────
/** `GET /projects/{id}/ingest` — snapshot; live updates via queue.updated. */
export const IngestQueueResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.string(),
    tasks: z.array(IngestTaskSchema),
    processing: z.boolean(),
});
/** `POST /projects/{id}/ingest/actions` — queue control. */
export const IngestQueueActionRequestSchema = z.object({
    action: z.enum([
        "retry",
        "cancel",
        "retry-all-failed",
        "cancel-all",
        "clear-completed",
    ]),
    /** Required for retry/cancel. */
    taskId: z.string().optional(),
});
// ─── CLI provider detection (F-084 adjunct, §11 runner) ──────────────────
export const CliToolSchema = z.enum(["claude", "codex", "gemini", "opencode"]);
/**
 * `GET /cli/detect/{tool}` — server-side probe of the runner image's CLI.
 * Body mirrors the desktop `claude_cli_detect` / `codex_cli_detect`
 * DetectResult so the vendored settings pill renders unchanged.
 */
export const CliDetectResponseSchema = z.object({
    ok: z.literal(true),
    installed: z.boolean(),
    version: z.string().nullable(),
    path: z.string().nullable(),
    error: z.string().nullable(),
});
/** Bare DetectResult (no envelope) — one entry of the /clis health map. */
export const CliDetectResultSchema = CliDetectResponseSchema.omit({ ok: true });
/**
 * `GET /clis` (runnerd :8095, F-077) — health probe of every pinned CLI
 * binary in the runner image (which-lookup + `--version`), consumed by
 * the settings UI health pills via the gateway `/cli/detect/{tool}` hop.
 */
export const ClisHealthResponseSchema = z.object({
    ok: z.literal(true),
    clis: z.record(CliToolSchema, CliDetectResultSchema),
});
// ─── Admin: users / tenants / quotas (dashboard admin screen) ────────────
export const AdminUserSchema = UserSchema.extend({
    lastLoginAt: IsoDateTimeSchema.nullable().optional(),
});
export const ListUsersResponseSchema = z.object({
    ok: z.literal(true),
    users: z.array(AdminUserSchema),
});
export const CreateUserRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(10),
    role: UserRoleSchema.default("member"),
});
export const UpdateUserRequestSchema = z.object({
    role: UserRoleSchema.optional(),
    /** Admin password reset. */
    password: z.string().min(10).optional(),
});
/**
 * `GET/PATCH /admin/tenant` — tenant admin view. Hard admission quotas
 * reuse the `TenantQuotasSchema` from ./jobs (tenant_quotas table, G-5).
 */
export const TenantAdminResponseSchema = z.object({
    ok: z.literal(true),
    tenant: TenantSchema,
    quotas: TenantQuotasSchema,
});
export const UpdateTenantRequestSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    planLimits: JsonObjectSchema.optional(),
    quotas: TenantQuotasSchema.omit({ tenantId: true }).partial().optional(),
});
// ─── Usage / metering (dashboard usage screen; cli_runs + jobs) ──────────
export const UsageQuerySchema = z.object({
    /** ISO date (inclusive); default = 30 days ago. */
    from: z.string().optional(),
    /** ISO date (inclusive); default = today. */
    to: z.string().optional(),
    projectId: UuidSchema.optional(),
});
/** One aggregated metering bucket (per day × metric × optional project). */
export const UsageRowSchema = z.object({
    /** ISO date of the bucket (UTC day). */
    day: z.string(),
    projectId: UuidSchema.nullable(),
    /** e.g. `llm.tokens_in`, `llm.tokens_out`, `cli.runs`, `cli.duration_ms`,
     *  `ingest.tasks`, `chat.requests`, `storage.bytes`. */
    metric: z.string(),
    value: z.number(),
});
export const UsageResponseSchema = z.object({
    ok: z.literal(true),
    from: z.string(),
    to: z.string(),
    rows: z.array(UsageRowSchema),
});
// ─── Route catalogue (informative; typed path builders) ──────────────────
export const ApiRoutes = {
    health: () => `${API_PREFIX}/health`,
    // auth
    login: () => `${API_PREFIX}/auth/login`,
    logout: () => `${API_PREFIX}/auth/logout`,
    me: () => `${API_PREFIX}/auth/me`,
    changePassword: () => `${API_PREFIX}/auth/password`,
    apiTokens: () => `${API_PREFIX}/auth/tokens`,
    apiToken: (id) => `${API_PREFIX}/auth/tokens/${id}`,
    // tenancy
    projects: () => `${API_PREFIX}/projects`,
    project: (ref) => `${API_PREFIX}/projects/${encodeURIComponent(ref)}`,
    openProject: (ref) => `${ApiRoutes.project(ref)}/open`,
    importProject: () => `${API_PREFIX}/projects/import`,
    recents: () => `${API_PREFIX}/users/me/recent-projects`,
    // desktop-compatible reads
    files: (ref) => `${ApiRoutes.project(ref)}/files`,
    fileContent: (ref) => `${ApiRoutes.project(ref)}/files/content`,
    // dashboard fs-funnel extensions (ADR-007)
    fileStat: (ref) => `${ApiRoutes.project(ref)}/files/stat`,
    fileBase64: (ref) => `${ApiRoutes.project(ref)}/files/base64`,
    filesCopy: (ref) => `${ApiRoutes.project(ref)}/files/copy`,
    filesDelete: (ref) => `${ApiRoutes.project(ref)}/files/delete`,
    sourcesRelated: (ref) => `${ApiRoutes.project(ref)}/sources/related`,
    fileChanges: (ref) => `${ApiRoutes.project(ref)}/file-changes`,
    fileChangeAction: (ref, taskId, action) => `${ApiRoutes.project(ref)}/file-changes/${encodeURIComponent(taskId)}/${action}`,
    ingestQueue: (ref) => `${ApiRoutes.project(ref)}/ingest`,
    ingestActions: (ref) => `${ApiRoutes.project(ref)}/ingest/actions`,
    ingestInteractive: (ref) => `${ApiRoutes.project(ref)}/ingest/interactive`,
    ingestInteractiveWrites: (ref, streamId) => `${ApiRoutes.project(ref)}/ingest/interactive/${encodeURIComponent(streamId)}/writes`,
    ingestInteractiveCancel: (ref, streamId) => `${ApiRoutes.project(ref)}/ingest/interactive/${encodeURIComponent(streamId)}/cancel`,
    cliDetect: (tool) => `${API_PREFIX}/cli/detect/${encodeURIComponent(tool)}`,
    // admin & usage (dashboard screens)
    adminUsers: () => `${API_PREFIX}/admin/users`,
    adminUser: (id) => `${API_PREFIX}/admin/users/${encodeURIComponent(id)}`,
    adminTenant: () => `${API_PREFIX}/admin/tenant`,
    usage: () => `${API_PREFIX}/usage`,
    reviews: (ref) => `${ApiRoutes.project(ref)}/reviews`,
    review: (ref, id) => `${ApiRoutes.project(ref)}/reviews/${encodeURIComponent(id)}`,
    reviewsReverify: (ref) => `${ApiRoutes.project(ref)}/reviews/reverify`,
    reviewsLog: (ref) => `${ApiRoutes.project(ref)}/reviews/log`,
    reviewsUndo: (ref, entryId) => `${ApiRoutes.project(ref)}/reviews/log/${encodeURIComponent(entryId)}/undo`,
    rotationStatus: (ref) => `${ApiRoutes.project(ref)}/rotation-status`,
    search: (ref) => `${ApiRoutes.project(ref)}/search`,
    graph: (ref) => `${ApiRoutes.project(ref)}/graph`,
    rescan: (ref) => `${ApiRoutes.project(ref)}/sources/rescan`,
    chat: (ref) => `${ApiRoutes.project(ref)}/chat`,
    conversations: (ref) => `${ApiRoutes.project(ref)}/conversations`,
    conversation: (ref, id) => `${ApiRoutes.project(ref)}/conversations/${encodeURIComponent(id)}`,
    conversationMessages: (ref, id) => `${ApiRoutes.project(ref)}/conversations/${encodeURIComponent(id)}/messages`,
    // sources / pages
    uploadSources: (ref) => `${ApiRoutes.project(ref)}/sources`,
    deleteSources: (ref) => `${ApiRoutes.project(ref)}/sources/delete`,
    enqueueIngest: (ref) => `${ApiRoutes.project(ref)}/ingest`,
    writePage: (ref) => `${ApiRoutes.project(ref)}/pages`,
    media: (ref) => `${ApiRoutes.project(ref)}/media`,
    // events & jobs
    events: (ref) => `${ApiRoutes.project(ref)}/events`,
    jobs: (ref) => `${ApiRoutes.project(ref)}/jobs`,
    jobEvents: (jobId) => `${API_PREFIX}/jobs/${jobId}/events`,
    // research
    research: (ref) => `${ApiRoutes.project(ref)}/research`,
    researchCancel: (ref, taskId) => `${ApiRoutes.project(ref)}/research/${encodeURIComponent(taskId)}/cancel`,
    researchOptimize: (ref) => `${ApiRoutes.project(ref)}/research/optimize`,
    // chat regenerate (F-175 lift)
    chatRegenerate: (ref) => `${ApiRoutes.project(ref)}/chat/regenerate`,
    // dedup suite (F-036…F-043)
    dedupScan: (ref) => `${ApiRoutes.project(ref)}/dedup/scan`,
    dedupScanCancel: (ref, jobId) => `${ApiRoutes.project(ref)}/dedup/scan/${encodeURIComponent(jobId)}/cancel`,
    dedupMerges: (ref) => `${ApiRoutes.project(ref)}/dedup/merges`,
    dedupNotDuplicates: (ref) => `${ApiRoutes.project(ref)}/dedup/not-duplicates`,
    dedupQueue: (ref) => `${ApiRoutes.project(ref)}/dedup`,
    dedupActions: (ref) => `${ApiRoutes.project(ref)}/dedup/actions`,
    // lint (F-044/F-045)
    lint: (ref) => `${ApiRoutes.project(ref)}/lint`,
    lintRun: (ref) => `${ApiRoutes.project(ref)}/lint/run`,
    // wiki-page lifecycle
    pagesDelete: (ref) => `${ApiRoutes.project(ref)}/pages/delete`,
    pagesEnrich: (ref) => `${ApiRoutes.project(ref)}/pages/enrich`,
    // embeddings
    embeddingsReindex: (ref) => `${ApiRoutes.project(ref)}/embeddings/reindex`,
    embeddingsStats: (ref) => `${ApiRoutes.project(ref)}/embeddings`,
    embeddingsDropLegacy: (ref) => `${ApiRoutes.project(ref)}/embeddings/drop-legacy`,
    // project zip export (F-212 web replacement for "open project folder")
    projectExport: (ref) => `${ApiRoutes.project(ref)}/export`,
    // source folder delete
    sourcesDeleteFolder: (ref) => `${ApiRoutes.project(ref)}/sources/delete-folder`,
    // clip
    clip: () => `${API_PREFIX}/clip`,
    projectClip: (ref) => `${ApiRoutes.project(ref)}/clip`,
    // git source connector (server extension)
    gitSources: (ref) => `${ApiRoutes.project(ref)}/git-sources`,
    gitSource: (ref, repoId) => `${ApiRoutes.project(ref)}/git-sources/${encodeURIComponent(repoId)}`,
    gitSourceSync: (ref, repoId) => `${ApiRoutes.project(ref)}/git-sources/${encodeURIComponent(repoId)}/sync`,
    gitSourceStatus: (ref, repoId) => `${ApiRoutes.project(ref)}/git-sources/${encodeURIComponent(repoId)}/status`,
    // config
    config: (key) => `${API_PREFIX}/config/${encodeURIComponent(key)}`,
    // connection tests
    connectionTest: (target) => `${API_PREFIX}/connection-tests/${encodeURIComponent(target)}`,
};
// ─── Misc shared param schemas ───────────────────────────────────────────
export const ProjectParamsSchema = z.object({
    projectId: ProjectRefSchema,
});
export const JobParamsSchema = z.object({
    jobId: UuidSchema,
});
/** Epoch-ms convenience re-export for API consumers. */
export { EpochMsSchema };
//# sourceMappingURL=api.js.map